import { BookingType } from "@collins/react-componments/dist/types/booking";
import { ChevronRight, Tag, X } from "lucide-react";
import { ViewBooking } from "collins-js-sdk";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { IoChevronBack } from "react-icons/io5";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { EDSSearch } from "../../../../eds";
import { EDSButton } from "../../../../eds/EDSButton";

type BookingTypeListType = {
  suppressHeading?: boolean;
  handleClose: () => void;
  updateBooking: (booking: Partial<ViewBooking>) => void;
};

const BookingTypeList = ({
  suppressHeading,
  handleClose,
  updateBooking
}: BookingTypeListType) => {
  const bookingContext = useDiaryContext();
  const [search, setSearch] = useState<string>("");

  const [filteredTypes, setFilteredTypes] = useState<BookingType[] | undefined>(
    bookingContext?.venueConfig?.booking_types?.filter((bt) => !bt.archived)
  );

  // Update filtered types when venue changes
  useEffect(() => {
    const availableTypes = bookingContext?.venueConfig?.booking_types?.filter(
      (bt) => !bt.archived
    );

    if (search) {
      // If there's a search term, filter by it
      setFilteredTypes(
        availableTypes?.filter((ft) =>
          ft.name.toLowerCase().includes(search.toLowerCase())
        )
      );
    } else {
      // Otherwise, show all available types
      setFilteredTypes(availableTypes);
    }
  }, [
    bookingContext?.venueConfig?.booking_types,
    bookingContext?.venueConfig?._id,
    search
  ]);

  const filterTypes = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchType = e.target.value;
    setSearch(searchType);
    setFilteredTypes(
      bookingContext.venueConfig?.booking_types
        ?.filter((ft) => !ft.archived)
        ?.filter((ft) =>
          ft.name.toLowerCase().includes(searchType.toLowerCase())
        )
    );
  };

  return (
    <motion.div
      className="h-full flex flex-col"
      initial="hidden"
      animate="visible"
      transition={{ duration: 0.25 }}
      variants={{
        visible: { opacity: 1 },
        hidden: { opacity: 0 }
      }}
    >
      {!suppressHeading && (
        <div className="flex justify-between items-center w-[305px] gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center">
            <IoChevronBack
              size={24}
              onClick={() => {
                handleClose();
              }}
            />
          </div>
          <h2 className="text-left font-bold text-base text-[var(--primaryColor)] m-0 flex-grow mt-0.5">
            Choose Booking Type
          </h2>
          <EDSButton
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close"
          >
            <X />
          </EDSButton>
        </div>
      )}
      <div className="flex flex-col flex-1 h-0">
        <div className="pt-3 pb-1">
          <EDSSearch
            value={search}
            role="search"
            placeholder="Search booking types"
            onChange={filterTypes}
            className="w-full"
          />
        </div>
        <div className="overflow-auto h-0 flex-grow [[data-theme='Light']_&]:text-foreground">
          {filteredTypes && filteredTypes.length > 0 ? (
            filteredTypes.map((data) => (
              <button
                type="button"
                className="w-full py-3.5 px-5 flex items-center justify-between border-b border-border cursor-pointer transition-all duration-150 ease-in-out bg-card gap-3 border-none text-left last:border-b-0 hover:bg-accent/50 active:bg-accent focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2 [&:hover_.booking-type-chevron]:translate-x-0.5 [&:hover_.booking-type-chevron]:text-foreground"
                key={data.id}
                onClick={() => {
                  updateBooking({
                    type: {
                      id: data.id,
                      name: data.name
                    }
                  });
                  handleClose();
                }}
              >
                <p className="text-left font-medium text-sm text-foreground m-0 flex-1">
                  {data.name}
                </p>
                <ChevronRight
                  className="booking-type-chevron text-muted-foreground transition-all duration-150 ease-in-out shrink-0"
                  size={20}
                />
              </button>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[25vh] text-center p-8">
              <Tag className="h-12 w-12 text-muted-foreground mb-6 opacity-50" />
              <h2 className="text-xl font-semibold mb-3">
                No Booking Types Found
              </h2>
              <p className="text-muted-foreground text-sm max-w-md">
                {search
                  ? `No booking types match "${search}".`
                  : "No booking types are available for this venue."}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo<BookingTypeListType>(BookingTypeList);
